import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, CreditCard, AlertTriangle, Check } from 'lucide-react';
import ModalShell from './ModalShell';
import Button from './Button';

const SubscriptionModal = ({ isOpen, onClose, variant = 'onboarding' }) => {
  const navigate = useNavigate();
  const isExpired = variant === 'expired';

  const handleSubscribe = () => {
    const onSubscriptionPage = window.location.pathname.startsWith('/vendor/subscription');

    if (isExpired) {
      if (!onSubscriptionPage) {
        navigate('/vendor/subscription');
      }
      onClose?.();
      return;
    }

    navigate('/vendor/subscription');
    onClose?.();
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={isExpired ? undefined : onClose}
      labelledBy="subscription-modal-title"
      panelClassName="max-w-md"
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-md border border-border bg-surface shadow-sm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="subscription-modal-title"
      >
        <div className="relative p-4 pb-3 sm:p-5 sm:pb-3">
          {!isExpired && (
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 rounded-md p-1 text-ink-tertiary transition-colors hover:bg-surface-muted hover:text-ink"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          )}

          <div className="mb-3 flex items-center justify-center">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-full border ${ isExpired ? 'border-amber-200 bg-amber-50' : 'border-border bg-surface-muted' }`}
            >
              {isExpired ? (
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              ) : (
                <CreditCard className="h-5 w-5 text-ink" />
              )}
            </div>
          </div>

          <h2
            id="subscription-modal-title"
            className="mb-1 text-center text-base font-semibold text-ink"
          >
            {isExpired ? 'Your subscription has ended' : 'Start Your Journey'}
          </h2>
          <p className="text-center text-xs leading-relaxed text-ink-secondary">
            {isExpired
              ? 'Upgrade your plan to accept new client bookings, manage orders, and use your vendor dashboard again.'
              : 'Choose a subscription plan to start offering your services and grow your business.'}
          </p>
        </div>

        <div className="px-4 pb-4 sm:px-5 sm:pb-5">
          {!isExpired && (
            <div className="mb-4 space-y-2">
              {[
                'Accept and manage client bookings',
                'Portfolio and service management',
                'Analytics dashboard',
                'Priority support',
              ].map((label) => (
                <div key={label} className="flex items-center gap-2">
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-surface-muted">
                    <Check className="h-3 w-3 text-ink" />
                  </div>
                  <span className="text-xs text-ink-secondary">{label}</span>
                </div>
              ))}
            </div>
          )}

          {isExpired && (
            <ul className="mb-4 list-inside list-disc space-y-1 text-xs text-ink-secondary">
              <li>You cannot accept or update client bookings</li>
              <li>Services, invoices, and payouts are paused</li>
              <li>Choose a plan below to restore full access</li>
            </ul>
          )}

          <div className="space-y-2">
            <Button type="button" variant="primary" size="md" fullWidth onClick={handleSubscribe}>
              {isExpired ? 'Upgrade plan now' : 'View plans & subscribe'}
            </Button>

            {!isExpired && (
              <Button type="button" variant="ghost" size="md" fullWidth onClick={onClose}>
                Maybe later
              </Button>
            )}
          </div>
        </div>
      </div>
    </ModalShell>
  );
};

export default SubscriptionModal;
